import { useCallback, useEffect, useRef, useState } from "react"
import { useCurrentUser, loadMe } from "@/features/auth/hooks/useCurrentUser"
import { useEntitlements } from "../hooks/useEntitlements"
import { planOf, priceCopy, usePlans, type PlanCode } from "../plan"
import { planName } from "../copy"
import { UNCONFIRMED_COPY, startSubscription, subscriptionApi, type PlanChoice, type SubscriptionState } from "../services/razorpayCheckout"

const dateLabel = (iso: string | null): string | null => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? null
    : new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Kolkata" }).format(d)
}

/** Plain-words status for the subscription state. */
function statusLabel(state: SubscriptionState | null, end: string | null): string {
  if (!state) return "Not subscribed"
  if (state.subscribed) {
    if (state.cancel_at_cycle_end) return end ? `Cancels on ${end}` : "Cancels at period end"
    return "Active"
  }
  switch (state.status) {
    case "cancelled":
    case "completed":
    case "expired":
      return "Cancelled"
    case "created":
    case "authenticated":
    case "pending":
      return "Payment pending"
    case "halted":
      return "Payment failed"
    default:
      return "Not subscribed"
  }
}

export type Pending = "subscribe" | "cancel" | null

/**
 * Everything a subscription screen needs, with no markup: the state from the server, the plan,
 * subscribe and cancel (with their confirm step), and what to say. The platform's panel below and
 * Nira's account page (features/nira2/account) both draw from it.
 */
export function useSubscription() {
  const { me } = useCurrentUser()
  const { subscribed, entitlements, isLoading } = useEntitlements()
  const [state, setState] = useState<SubscriptionState | null>(null)
  const plans = usePlans()
  const plan = planOf(plans, "pro")
  // The plan the reader is on (Plus or Pro), else Pro: what the price line speaks of.
  const code: PlanCode = entitlements?.plan_code === "plus" ? "plus" : "pro"
  const current = planOf(plans, code)
  const [pending, setPending] = useState<Pending>(null)
  const [message, setMessage] = useState("")
  const [confirming, setConfirming] = useState(false)
  const inFlight = useRef(false)
  const busy = pending !== null

  const refresh = useCallback(async () => {
    try {
      setState(await subscriptionApi.state())
    } catch {
      setMessage("Could not load your subscription.")
    }
  }, [])
  useEffect(() => { void refresh() }, [refresh, subscribed])

  const run = async (kind: Exclude<Pending, null>, fn: () => Promise<void>) => {
    if (inFlight.current) return
    inFlight.current = true
    setPending(kind)
    setMessage("")
    try {
      await fn()
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Something went wrong")
    } finally {
      inFlight.current = false
      setPending(null)
    }
  }

  const subscribe = (choice: PlanChoice = "pro") => run("subscribe", async () => {
    const r = await startSubscription(choice, { name: me?.display_name, email: me?.email }, setMessage)
    if (r === "active") setMessage("You are subscribed. Thank you!")
    else if (r === "dismissed") setMessage("Checkout was closed. You can subscribe again any time.")
    else if (r === "unconfirmed") setMessage(UNCONFIRMED_COPY)
    else if (r === "pending") setMessage("Payment received. Activation is taking longer than usual.")
    await Promise.all([refresh(), loadMe(true)])
  })

  const cancel = () => run("cancel", async () => {
    setConfirming(false)
    await subscriptionApi.cancel()
    await Promise.all([refresh(), loadMe(true)])
  })

  const end = dateLabel(state?.current_end ?? null)
  const cancelling = state?.subscribed === true && state.cancel_at_cycle_end === true
  const cancelled = !state?.subscribed && state?.status === "cancelled"
  const loading = isLoading && !state
  return {
    state, plan, plans, pending, busy, message, confirming, setConfirming, subscribe, cancel,
    end, cancelling, cancelled, loading, status: statusLabel(state, end), price: priceCopy(current, code), planLabel: planName(entitlements?.plan_state, subscribed), entitlements,
  }
}

/** Subscription status, renewal date, subscribe and cancel. Renders inside the profile modal or the /subscription page. */
export function SubscriptionPanel() {
  const { state, pending, busy, message, confirming, setConfirming, subscribe, cancel, end, cancelling, cancelled, loading, price, planLabel } = useSubscription()

  return (
    <div className="sub-panel">
      {loading ? <p role="status">Loading…</p> : (
        <section aria-label="Plan" className="sub-panel__details">
          <div className="sub-page__row"><span className="sub-page__k">Plan</span><span>{state?.subscribed ? planLabel : "Free"}</span></div>
          <div className="sub-page__row"><span className="sub-page__k">Status</span><span>{statusLabel(state, end)}</span></div>
          <div className="sub-page__row"><span className="sub-page__k">Price</span><span>{price}</span></div>
          {end && state?.subscribed && !cancelling && (
            <div className="sub-page__row"><span className="sub-page__k">Renews on</span><span>{end}</span></div>
          )}
          {end && cancelled && (
            <div className="sub-page__row"><span className="sub-page__k">Access until</span><span>{end}</span></div>
          )}
        </section>
      )}

      {cancelling && (
        <p className="sub-panel__note">
          {end ? `Your subscription ends on ${end}. ` : "Your subscription ends at the end of the current period. "}
          You keep access until then and it will not renew.
        </p>
      )}

      {message && <p role="alert" className="sub-panel__note">{message}</p>}

      {state?.subscribed ? (
        cancelling ? null : confirming ? (
          <div className="sub-panel__confirm" role="alertdialog" aria-labelledby="cancel-title" aria-describedby="cancel-desc">
            <h3 id="cancel-title" className="sub-panel__title">Cancel your subscription?</h3>
            <p id="cancel-desc" className="sub-panel__note">
              {end ? `You keep access until ${end}. It will not renew after that.` : "You keep access until the end of the current period. It will not renew after that."}
            </p>
            <div className="sub-panel__actions">
              <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => setConfirming(false)}>Keep subscription</button>
              <button type="button" className="btn btn--primary btn--sm" disabled={busy} aria-busy={pending === "cancel"} onClick={() => { void cancel() }}>
                {pending === "cancel" && <span className="sub-panel__spinner" aria-hidden="true" />}
                {pending === "cancel" ? "Cancelling…" : "Yes, cancel"}
              </button>
            </div>
          </div>
        ) : (
          <div className="sub-panel__actions">
            <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => setConfirming(true)}>
              Cancel subscription
            </button>
          </div>
        )
      ) : (
        <div className="sub-panel__actions">
          <button type="button" className="btn btn--primary btn--sm" disabled={busy} aria-busy={pending === "subscribe"} onClick={() => { void subscribe("pro") }}>
            {pending === "subscribe" && <span className="sub-panel__spinner" aria-hidden="true" />}
            {pending === "subscribe" ? "Working…" : "Subscribe"}
          </button>
        </div>
      )}
    </div>
  )
}
